
import { useState, useEffect, useCallback } from 'react';
import { filterEvents } from '@/lib/events';
import { Event, EventFilters, EventType } from '@/lib/types';
import { createDefaultDateRangeFilter } from './calendarHelpers';

// Létrehozunk egy állandót a localStorage kulcsának
const FILTER_STORAGE_KEY = 'medihub-filter-settings';

// Segédfüggvény a mentett szűrők betöltéséhez
const loadSavedFilters = (): EventFilters | null => {
  try {
    const savedFiltersString = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!savedFiltersString) return null;
    
    const savedFilters = JSON.parse(savedFiltersString);
    
    // Átalakítjuk a mentett formátumot EventFilters formátumra és biztosítjuk a helyes típusokat
    const activeTypes = Object.entries(savedFilters.selectedTypes)
      .filter(([_, isSelected]) => isSelected)
      .map(([type]) => type as EventType);
      
    const activeProfessions = Object.entries(savedFilters.selectedProfessions)
      .filter(([_, isSelected]) => isSelected)
      .map(([profession]) => profession);
    
    return {
      search: savedFilters.search,
      location: savedFilters.location,
      type: activeTypes,
      professions: activeProfessions,
      hasCreditPoints: savedFilters.hasCreditPoints,
      dateRange: savedFilters.dateRange
    };
  } catch (error) {
    console.error('Hiba a mentett szűrők betöltésekor:', error);
    return null;
  }
};

export function useEventFiltering(
  allEvents: Event[], 
  calendarStartDate: Date, 
  calendarEndDate: Date
) {
  // Kezdeti állapot beállítása a mentett szűrőkkel vagy üres objektummal
  const initialFilters = loadSavedFilters() || {};
  const [filters, setFilters] = useState<EventFilters>(initialFilters);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [totalFilteredEvents, setTotalFilteredEvents] = useState(0);

  // Memoize the filter change handler
  const handleFilterChange = useCallback((newFilters: EventFilters) => {
    setFilters(prevFilters => {
      // Only update if filters actually changed to prevent unnecessary re-renders
      if (JSON.stringify(prevFilters) === JSON.stringify(newFilters)) {
        return prevFilters;
      }
      return newFilters;
    });
  }, []);

  // Apply filters effect
  useEffect(() => {
    console.log('🔍 Filtering events...', {
      allEventsCount: allEvents.length,
      calendarStartDate: calendarStartDate?.toISOString(),
      calendarEndDate: calendarEndDate?.toISOString(),
      currentFilters: filters
    });

    // Don't filter if we don't have valid calendar dates yet
    if (!calendarStartDate || !calendarEndDate) {
      console.log('⏳ Waiting for calendar dates to be initialized...');
      return;
    }

    // Create a complete filter object
    const completeFilters: EventFilters = { ...filters };

    // Check if any actual filters are applied (not just default empty filters)
    const hasActiveFilters = (
      (completeFilters.type && completeFilters.type.length > 0) ||
      (completeFilters.professions && completeFilters.professions.length > 0) ||
      (completeFilters.location && completeFilters.location.length > 0) ||
      completeFilters.hasCreditPoints ||
      (completeFilters.search && completeFilters.search.length > 0)
    );

    // If no active filters and no date range, use a very inclusive date range
    if (!hasActiveFilters && !completeFilters.dateRange) {
      completeFilters.dateRange = createDefaultDateRangeFilter(calendarStartDate, calendarEndDate);
      console.log('📅 Using default inclusive date range:', completeFilters.dateRange);
    }

    // Apply all filters at once
    const filtered = filterEvents(allEvents, completeFilters);
    console.log('✂️ Events after filtering:', filtered.length, 'out of', allEvents.length);

    // Log first few events for debugging
    if (allEvents.length > 0) {
      console.log('📋 Sample events dates:', allEvents.slice(0, 3).map(e => ({
        name: e.name,
        dateStart: e.dateStart,
        dateEnd: e.dateEnd
      })));
    }

    // Update both states at once to prevent multiple re-renders
    setTotalFilteredEvents(filtered.length);
    setFilteredEvents(filtered);
  }, [filters, calendarStartDate, calendarEndDate, allEvents]);

  return {
    filters,
    filteredEvents,
    totalFilteredEvents,
    handleFilterChange
  };
}
